import type { ReactNode } from 'react';

import { AdminUrls, PermissionCodes } from '@treneo/features/admin';
import { useCurrentUser } from '@treneo/features/user';
import { getApiBaseUrl } from '@treneo/infrastructure/api';
import { useAppTranslations } from '@treneo/shared/i18n';
import {
  FormatListBulletedIcon,
  HealthIcon,
  OrganizationIcon,
  PermissionsIcon,
  RefreshIcon,
  ScheduleIcon,
  StorageIcon,
  TimelineIcon,
  UsersIcon
} from '@treneo/shared/icons';
import { useAppLocation, useAppNavigate } from '@treneo/shared/router';
import { Colors } from '@treneo/shared/themes';
import {
  Drawer,
  List,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText
} from '@treneo/shared/ui';

const drawerWidth = 240;

type MenuItem = {
  label: string;
  path: string;
  icon: ReactNode;
  permissionCode: string;
  external?: boolean;
  isOrganizationSpecific?: boolean;
  alsoShowForGlobalPermission?: string;
  getUrl?: () => string;
};

const getHangfireUrl = (): string => {
  if (import.meta.env.DEV) {
    return `${getApiBaseUrl()}/hangfire`;
  }

  return `${globalThis.window.location.origin}/api/hangfire`;
};

const getMenuItems = (t: (key: string) => string): MenuItem[] => [
  {
    label: t('admin.hangfire'),
    path: '',
    icon: <ScheduleIcon />,
    permissionCode: PermissionCodes.ManageHangfire,
    external: true,
    getUrl: getHangfireUrl
  },
  {
    label: t('admin.healthCheck'),
    path: AdminUrls.health,
    icon: <HealthIcon />,
    permissionCode: PermissionCodes.ViewHealth
  },
  {
    label: t('admin.permissions'),
    path: AdminUrls.permissions,
    icon: <PermissionsIcon />,
    permissionCode: PermissionCodes.ManageIdentity
  },
  {
    label: t('admin.users'),
    path: AdminUrls.users,
    icon: <UsersIcon />,
    permissionCode: PermissionCodes.ManageIdentity
  },
  {
    label: t('admin.loginActivity.menuItem'),
    path: AdminUrls.loginActivity,
    icon: <TimelineIcon />,
    permissionCode: PermissionCodes.ManageIdentity
  },
  {
    label: t('admin.invitationsLabel'),
    path: AdminUrls.invitations,
    icon: <FormatListBulletedIcon />,
    permissionCode: PermissionCodes.ManageIdentity
  },
  {
    label: t('admin.organizationsLabel'),
    path: AdminUrls.organizations,
    icon: <OrganizationIcon />,
    permissionCode: PermissionCodes.ManageOrganization,
    isOrganizationSpecific: true
  },
  {
    label: t('admin.cache.title'),
    path: AdminUrls.cache,
    icon: <StorageIcon />,
    permissionCode: PermissionCodes.ManageCache
  },
  {
    label: t('admin.syncPage.title'),
    path: AdminUrls.synchronization,
    icon: <RefreshIcon />,
    permissionCode: PermissionCodes.ManageSynchronization
  }
];

type AdminSidebarProps = {
  isOpen: boolean;
  onClose: () => void;
};

export const AdminSidebar = ({ isOpen, onClose }: Readonly<AdminSidebarProps>) => {
  const { t } = useAppTranslations();
  const navigate = useAppNavigate();
  const location = useAppLocation();
  const { hasPermission, hasPermissionInAnyOrganization } = useCurrentUser();

  const menuItems = getMenuItems(t);

  const visibleMenuItems = menuItems.filter(item => {
    if (item.isOrganizationSpecific) {
      const hasInAnyOrg = hasPermissionInAnyOrganization(item.permissionCode);
      const hasGlobalAlternative =
        item.alsoShowForGlobalPermission != null && hasPermission(item.alsoShowForGlobalPermission);
      return hasInAnyOrg || hasGlobalAlternative === true;
    }
    return hasPermission(item.permissionCode);
  });

  const handleNavigation = (item: MenuItem) => {
    if (item.external) {
      const url = item.getUrl ? item.getUrl() : item.path;
      window.open(url, '_blank', 'noopener,noreferrer');
    } else {
      navigate(item.path);
    }
  };

  const renderedMenuItems = visibleMenuItems.map(item => {
    const isActive =
      !item.external &&
      (location.pathname === item.path || location.pathname.startsWith(`${item.path}/`));
    return (
      <ListItem key={item.path} disablePadding>
        <ListItemButton selected={isActive} onClick={() => handleNavigation(item)}>
          <ListItemIcon>{item.icon}</ListItemIcon>
          <ListItemText primary={item.label} />
        </ListItemButton>
      </ListItem>
    );
  });

  return (
    <Drawer
      variant='permanent'
      open={isOpen}
      onClose={onClose}
      sx={{
        width: drawerWidth,
        flexShrink: 0,
        '& .MuiDrawer-paper': {
          width: drawerWidth,
          boxSizing: 'border-box',
          borderRight: 'none',
          position: 'relative',
          top: 0,
          left: 0,
          height: '100%',
          backgroundColor: Colors.lightGrey
        }
      }}
    >
      <List
        sx={{
          pt: 0,
          mt: 0,
          marginTop: '1rem'
        }}
      >
        {renderedMenuItems}
      </List>
    </Drawer>
  );
};
